/** Day-of-week instrument + ITM offset rule for the NSS bot.
 *
 * Documented in docs/design.md section 5.4.1:
 *   Monday    -> NIFTY  ITM 2
 *   Tuesday   -> NIFTY  ITM 2
 *   Wednesday -> SENSEX ITM 2
 *   Thursday  -> SENSEX ITM 2
 *   Friday    -> NIFTY  ITM 1
 *   Sat / Sun -> NIFTY  ITM 2 (markets closed; UI fallback)
 *
 * Defaults bundled per instrument so the trading-controls card can
 * auto-fill SL / trail values when the day flips.
 */

export type Instrument = "NIFTY" | "SENSEX";

export interface InstrumentInfo {
  name: Instrument;
  /** Kite symbol used by /api/broker/kite/ltp. */
  symbol: string;
  /** ATM rounding step in points. */
  strikeStep: number;
  /** Quantity per lot. */
  lotSize: number;
  /** Sizing default, in sync with backend contract_planner.py. SENSEX is
   * a hard day rule (15 lots, seeded every Wed/Thu); NIFTY days actually
   * seed each strategy's own saved default — 10 is the UI fallback. */
  defaultLots: number;
  defaultSlPoints: number;
  defaultTrailTrigger: number;
  defaultTrailAmount: number;
  /** Rung-1 cost buffer (2026-07-15 ladder rework): the first trail step
   * parks the SL at fill + this many points. NIFTY 1, SENSEX 3. Must stay
   * in sync with backend contract_planner.py UnderlyingSpec. */
  defaultTrailSlippage: number;
  /** Shadow-exit fixed-target simulation default (P50). */
  defaultTargetPoints: number;
}

export interface DayPlan {
  instrument: InstrumentInfo;
  /** ITM offset (in strikes) per the documented day rule. */
  itm: 1 | 2;
}

const NIFTY: InstrumentInfo = {
  name: "NIFTY",
  symbol: "NSE:NIFTY 50",
  strikeStep: 50,
  lotSize: 75,
  defaultLots: 10,
  defaultSlPoints: 10,
  defaultTrailTrigger: 10,
  defaultTrailAmount: 11,
  defaultTrailSlippage: 1,
  defaultTargetPoints: 31,
};

const SENSEX: InstrumentInfo = {
  name: "SENSEX",
  symbol: "BSE:SENSEX",
  strikeStep: 100,
  lotSize: 20,
  defaultLots: 15,
  defaultSlPoints: 35,
  defaultTrailTrigger: 22,
  defaultTrailAmount: 23,
  defaultTrailSlippage: 3,
  defaultTargetPoints: 70,
};

/** Returns the instrument + ITM for a given date. Day-index 0=Sun, 1=Mon, ..., 6=Sat. */
export function dayPlan(date: Date): DayPlan {
  switch (date.getDay()) {
    case 3:
    case 4:
      return { instrument: SENSEX, itm: 2 };
    case 5:
      return { instrument: NIFTY, itm: 1 };
    default:
      // Mon/Tue and weekend fallback.
      return { instrument: NIFTY, itm: 2 };
  }
}

/** Backwards-compatible helper. Prefer `dayPlan()` for new code. */
export function instrumentForDay(date: Date): InstrumentInfo {
  return dayPlan(date).instrument;
}

/** Round spot to nearest strike step (50 for NIFTY, 100 for SENSEX). */
export function atmStrike(spot: number, step: number): number {
  return Math.round(spot / step) * step;
}

/** ITM strike for a given side (CE buys ITM below spot; PE buys ITM above). */
export function itmStrike(
  spot: number,
  info: InstrumentInfo,
  itm: 1 | 2,
  side: "CE" | "PE",
): number {
  const atm = atmStrike(spot, info.strikeStep);
  const offset = info.strikeStep * itm;
  return side === "CE" ? atm - offset : atm + offset;
}

/** Short human label for the day rule, used as a sub-line in the UI. */
export function dayRuleLabel(date: Date): string {
  const weekday = date.toLocaleDateString(undefined, { weekday: "long" });
  return `${weekday} rule`;
}

export const INSTRUMENTS = { NIFTY, SENSEX } as const;
